import { createDateTimeOffset, fromLocalDate } from "DateTimeOffset";
import { getAvailableResult, type DependencyResult } from "DependencyResult";
import type { DependencyArgument, DependencyVisitorContext } from "DependencyVisitorContext";
import { mapData, mapDataOne, operationTypes, type MappedReturnType } from "DependencyVisitorUtils";
import { DependencyError } from "Errors";
import { ensureIsFiniteNumber } from "NumericService";

const dateMethodDecorationInfo = Object.freeze({
  operationType: operationTypes.Method,
  operandCount: 1,
});

function trimMilliseconds<Mode>(
  value: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): MappedReturnType<Mode, Date | null> {
  return mapDataOne(value, data, context, trimMillisecondsCore, dateMethodDecorationInfo);
}

function trimMillisecondsCore(value: unknown): DependencyResult<Date | null> {
  const result = cloneValue("TrimMilliseconds", value);
  if (result) {
    result.setMilliseconds(0);
  }

  return getAvailableResult(result);
}

function trimSeconds<Mode>(
  value: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): MappedReturnType<Mode, Date | null> {
  return mapDataOne(value, data, context, trimSecondsCore, dateMethodDecorationInfo);
}

function trimSecondsCore(value: unknown): DependencyResult<Date | null> {
  const result = cloneValue("TrimSeconds", value);
  if (result) {
    result.setSeconds(0);
    result.setMilliseconds(0);
  }

  return getAvailableResult(result);
}

function date<Mode>(
  value: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): MappedReturnType<Mode, Date | null> {
  return mapDataOne(value, data, context, dateCore, dateMethodDecorationInfo);
}

function dateCore(value: unknown): DependencyResult<Date | null> {
  let result = ensureDate("Date", value);
  if (result) {
    const year = result.getFullYear();
    result = new Date(year, result.getMonth(), result.getDate());
    result.setFullYear(year);
  }

  return getAvailableResult(result);
}

function dateTime<Mode>(
  value: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): MappedReturnType<Mode, Date | null> {
  return mapDataOne(value, data, context, dateTimeCore, dateMethodDecorationInfo);
}

function dateTimeCore(value: unknown): DependencyResult<Date | null> {
  let result = ensureDate("DateTime", value);
  if (result) {
    const year = result.getFullYear();
    result = new Date(
      year,
      result.getMonth(),
      result.getDate(),
      result.getHours(),
      result.getMinutes(),
      result.getSeconds(),
      result.getMilliseconds(),
    );
    result.setFullYear(year);
  }

  return getAvailableResult(result);
}

function utcDateTime<Mode>(
  value: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): MappedReturnType<Mode, Date | null> {
  return mapDataOne(value, data, context, utcDateTimeCore, dateMethodDecorationInfo);
}

function utcDateTimeCore(value: unknown): DependencyResult<Date | null> {
  let result = ensureDate("UtcDateTime", value);
  if (result) {
    result = fromLocalDate(result, "Z");
  }

  return getAvailableResult(result);
}

const addDaysDecorationInfo = Object.freeze({
  operationType: operationTypes.Method,
  operandCount: 2,
});

function addDays<Mode>(
  value: DependencyArgument,
  addDay: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): MappedReturnType<Mode, Date | null> {
  return mapData([value, addDay], data, context, addDaysCore, addDaysDecorationInfo);
}

function addDaysCore([value, addDay]: unknown[]): DependencyResult<Date | null> {
  const result = cloneValue("AddDays", value);
  if (result) {
    result.setDate(result.getDate() + (addDay != null ? ensureIsFiniteNumber(addDay) : 0));
  }
  return getAvailableResult(result);
}

function ensureDate(description: string, value: unknown): Date | null {
  if (value == null) {
    return null;
  }
  if (!(value instanceof Date)) {
    throw new DependencyError(`${description}() can only be used on a date.`);
  }

  return value;
}

function cloneValue(description: string, value: unknown): Date | null {
  let result: MaybeDateTimeOffset | null = ensureDate(description, value);
  if (result) {
    result = result.hasOffset ? createDateTimeOffset(result, -result.getTimezoneOffset()) : new Date(result.getTime());
  }
  return result;
}

type MaybeDateTimeOffset = Date & { hasOffset?: boolean };

export default { trimMilliseconds, trimSeconds, date, dateTime, utcDateTime, addDays };
